import {
  ChatBubbleOutlineOutlined,
  ErrorOutline,
  MarkEmailRead,
} from "@mui/icons-material";
import { Divider, List, Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Skeletons from "../../../components/loader/Skeletons";
import usePrimaryColor from "../../../hooks/usePrimaryColor";
import { isArrayEmpty, useIsSmallerScreen } from "../../../utils/Functions";
import Conversation from "../components/Conversation";
import ConversationFilter from "../components/ConversationFilter";
import OpenConversation from "../components/OpenConversation";
import useGetConvos from "../hooks/useGetConvos";
import MessagesWindowMobile from "./MessagesWindowMobile";

export default function InboxMobile() {
  const isSmallScreen = useIsSmallerScreen();
  const [searchParams] = useSearchParams();
  const convoId = searchParams.get("convo");
  const filterOnlyUnread = searchParams.get("read_f") === "false";

  const {
    data,
    isLoading: loadingConvos,
    error,
  } = useGetConvos(convoId == null);

  const dataEmpty = isArrayEmpty(data) || data == undefined || data == null;
  const [activeConvoId, setActiveConvoId] = React.useState(convoId);

  const filteredConvos = filterOnlyUnread
    ? data?.filter((conv) => Boolean(conv?.read) === false)
    : data;

  const ConversationList = React.memo(({ conv }) => {
    return <Conversation conv={conv} />;
  });

  React.useEffect(() => {
    setActiveConvoId(convoId);
  }, [convoId]);

  if (!isSmallScreen) {
    return null;
  }

  if (loadingConvos) {
    return <Loader />;
  } else if (!loadingConvos && error) {
    return <Error />;
  } else if (!loadingConvos && dataEmpty) {
    return <NoData />;
  }

  return (
    <Wrapper>
      {activeConvoId ? (
        <>
          <MessagesWindowMobile convoId={activeConvoId} />
          <List sx={{ flexGrow: 1, overflowY: "auto", width: "100%" }}>
            {filteredConvos?.map((conv) => (
              <ConversationList key={conv?.id} conv={conv} />
            ))}
          </List>
        </>
      ) : (
        <List sx={{ flexGrow: 1, overflowY: "auto", width: "100%" }}>
          {filteredConvos?.map((conv) => (
            <ConversationList key={conv?.id} conv={conv} />
          ))}
        </List>
      )}
    </Wrapper>
  );
}

function Loader() {
  return (
    <Wrapper>
      <Skeletons numOfSkeletons={6} grid={false} />
    </Wrapper>
  );
}

function NoData() {
  const { t } = useTranslation();
  const color = usePrimaryColor();
  const [searchParams] = useSearchParams();
  const isUnreadFilter = searchParams.get("read_f") ?? false;

  return (
    <Wrapper>
      <Stack
        spacing={2}
        alignItems="center"
        justifyContent="center"
        sx={{ flex: 1, py: 6, px: 2 }}
      >
        {isUnreadFilter ? (
          <MarkEmailRead sx={{ color: color }} fontSize="large" />
        ) : (
          <ChatBubbleOutlineOutlined sx={{ color: color }} fontSize="large" />
        )}
        <Typography variant="h6" color="text.secondary">
          {t(isUnreadFilter ? "AllMessagesHaveBeenRead" : "NoConversationsYet")}
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          {t(isUnreadFilter ? "" : "ClickinOnPlusToStartNewConvo")}
        </Typography>
      </Stack>
    </Wrapper>
  );
}
function Error() {
  const { t } = useTranslation();
  return (
    <Wrapper>
      <Stack
        spacing={2}
        alignItems="center"
        justifyContent="center"
        sx={{ flex: 1, py: 6, px: 2 }}
      >
        <ErrorOutline color="error" fontSize="large" />
        <Typography align="center" variant="h6" color="error">
          {t("SomethingWentWrong")}
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          {t("UnableToLoadConvos")}
        </Typography>
      </Stack>
    </Wrapper>
  );
}

function Wrapper({ children }) {
  const { t } = useTranslation();

  return (
    <Stack justifyContent={"center"} height={"100%"} alignItems={"center"}>
      <Stack
        p={1}
        alignItems={"center"}
        direction={"row"}
        justifyContent={"space-between"}
        width={"100%"}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          {t("Conversations")}
        </Typography>
        <OpenConversation />
      </Stack>
      <ConversationFilter />
      <Divider sx={{ width: "100%" }} />
      {children}
    </Stack>
  );
}
